import React, { useEffect, useState } from "react";
import { Waves, Warning } from "@phosphor-icons/react";
import { fetchDebrisFlowRisk } from "../lib/api";

const LEVEL_META = {
  elevated: { label: "ELEVATED", color: "#E63946" },
  advisory: { label: "ADVISORY", color: "#F59E0B" },
};

const fmtWindow = (iso) =>
  new Date(iso).toLocaleString([], { weekday: "short", hour: "numeric" });

export default function DebrisFlowCard({ county }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDebrisFlowRisk(county).then(setData).catch(() => setData(null));
    const t = setInterval(
      () => fetchDebrisFlowRisk(county).then(setData).catch(() => {}),
      10 * 60 * 1000,
    );
    return () => clearInterval(t);
  }, [county]);

  if (!data || !data.scars?.length) return null;
  const atRisk = data.at_risk || [];

  return (
    <section className="border border-tac-border bg-tac-surface/40" data-testid="debris-flow-card">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-tac-border/60">
        <Waves size={14} weight="duotone" className={atRisk.length ? "text-tac-danger" : "text-tac-info"} />
        <span className="text-[12px] uppercase tracking-[0.15em] font-bold text-tac-text">
          Burn Scar Debris-Flow Monitor
        </span>
        {atRisk.length > 0 && (
          <span className="text-[15px] font-mono-tac tabular text-tac-danger">· {atRisk.length}</span>
        )}
      </div>
      {atRisk.length === 0 ? (
        <div className="px-4 py-2.5 text-[13px] text-tac-dim" data-testid="debris-flow-none">
          Monitoring {data.scars_monitored} burn scars — no rain-driven debris-flow risk in the next 48 hours.
        </div>
      ) : (
        <ul className="divide-y divide-tac-border/40" data-testid="debris-flow-list">
          {atRisk.map((s) => {
            const m = LEVEL_META[s.risk_level] || LEVEL_META.advisory;
            return (
              <li key={s.scar_id} className="px-4 py-2.5" data-testid={`debris-flow-${s.scar_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border"
                    style={{ color: m.color, borderColor: `${m.color}66`, background: `${m.color}14` }}
                  >
                    <Warning size={10} weight="fill" /> {m.label}
                  </span>
                  <span className="text-[14px] font-bold text-tac-text">{s.name} burn scar</span>
                  {s.county && (
                    <span className="text-[11px] font-mono-tac text-tac-faint">{s.county} Co.</span>
                  )}
                </div>
                <div className="text-[13px] text-tac-dim mt-1 leading-snug">
                  Up to {s.peak_6h_in}" rain in 6 hrs ({s.max_pop}% chance), around {fmtWindow(s.window_start)}.
                  Mudflows and flash flooding possible in and below the burn area.
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="px-4 py-1.5 text-[11px] font-mono-tac text-tac-faint border-t border-tac-border/40">
        NWS 48-hr rain forecast over this season's fire perimeters · advisory only — follow county EM for official warnings
      </div>
    </section>
  );
}
